import { calculateSplit } from 'utils/shared/trades/calculateSplit';
import { normalizeCallPut } from 'utils/shared/trades/normalizeCallPut';
import { CallPut, type Trade } from 'utils/shared/trades/types';

type CallPutMixTrade = Pick<Trade, 'cp' | 'size' | 'premium'>;

export interface CallPutMixBreakdown {
  callContracts: number;
  putContracts: number;
  totalContracts: number;
  callShare: number;
  putShare: number;
  callPercentage: number;
  putPercentage: number;
  /** Premium behind each side — only ever used as a per-contract ratio, never as a split. */
  callPremium: number;
  putPremium: number;
}

export const calculateCallPutMixFromValues = (
  callContracts = 0,
  putContracts = 0,
  callPremium = 0,
  putPremium = 0,
): CallPutMixBreakdown => {
  const split = calculateSplit(callContracts, putContracts);

  return {
    callContracts: split.primary,
    putContracts: split.secondary,
    totalContracts: split.total,
    callShare: split.primaryShare,
    putShare: split.secondaryShare,
    callPercentage: split.primaryPercentage,
    putPercentage: split.secondaryPercentage,
    callPremium: Math.max(Number(callPremium) || 0, 0),
    putPremium: Math.max(Number(putPremium) || 0, 0),
  };
};

/**
 * Call vs put contract volume across the visible flow, plus the premium behind each side.
 *
 * The split is measured in CONTRACTS, deliberately. Under the default buy-side
 * trade-side filter `getDirectionalFlowSign` makes every call bullish and every put
 * bearish, so a premium-based call/put split would be an exact restatement of the net
 * premium card beside it. Contracts stay independent under every filter.
 *
 * Premium is carried only so the card can show average premium per contract — a ratio
 * of premium to contracts, which the net premium card cannot contain at all because it
 * has no contract axis. One order of 1 contract at $500k and one of 5,000 at $100 look
 * identical there and land at opposite ends of this figure.
 *
 * Composition read, not a directional one, so midpoint prints count here the same way
 * they count toward the trade table's totals — see `getTradeMatchesFlowSideBasis`.
 */
export const calculateCallPutMix = (trades: CallPutMixTrade[]): CallPutMixBreakdown => {
  let callContracts = 0;
  let putContracts = 0;
  let callPremium = 0;
  let putPremium = 0;

  trades.forEach((trade) => {
    const contracts = trade.size || 0;
    const premium = trade.premium || 0;
    const cp = normalizeCallPut(trade.cp);

    if (cp === CallPut.Call) {
      callContracts += contracts;
      callPremium += premium;
    } else if (cp === CallPut.Put) {
      putContracts += contracts;
      putPremium += premium;
    }
  });

  return calculateCallPutMixFromValues(callContracts, putContracts, callPremium, putPremium);
};
